import { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '@/store';
import ErrorBoundary from '@components/tailwind/ErrorBoundary';
import cx from '@utils/cx.util';
import { useVariation } from '@hooks/useVariation';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Configure dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const iconsMap = {
  inStock: require('@features/pdp/images/icons/icon-instock.svg'),
  preOrder: require('@features/pdp/images/icons/icon-preorder.svg'),
  lowStock: require('@features/pdp/images/icons/icon-preorder.svg'),
  soldOut: require('@features/pdp/images/icons/icon-oos.svg'),
};

const labelMap = {
  inStock: 'In Stock',
  preOrder: 'Pre-Order',
  lowStock: 'Low Stock',
  soldOut: 'Out of Stock',
};

export const ProductAvailability = ({
  stock,
  quantityPerOrderLimit = null,
  processingDays = 0,
  className = '',
}) => {
  return (
    <div
      className={cx(
        'tw:flex tw:items-start tw:gap-[6px] tw:rounded-button tw:border tw:border-neutrals-light-grey tw:p-2',
        className
      )}>
      <div className='tw:flex tw:size-6 tw:shrink-0 tw:items-center tw:justify-center'>
        <img src={iconsMap[stock]} alt={stock} className='tw:max-h-full tw:w-full tw:max-w-full' />
      </div>
      <div className='tw:flex tw:flex-col tw:gap-1'>
        <p className='tw:mb-0 tw:text-sm tw:font-medium tw:leading-none tw:text-body'>{labelMap[stock]}</p>

        {['inStock', 'lowStock'].includes(stock) && (
          <div className='tw:mb-0 tw:text-[13px] tw:font-normal tw:leading-none tw:text-body'>
            {(processingDays ?? 0) > 0 ? (
              <>
                Please allow up to {processingDays} {processingDays === 1 ? 'day' : 'days'} to process your
                order
              </>
            ) : (
              <ErrorBoundary message='Ready to Ship' messageOnly>
                <CutoffLabel />
              </ErrorBoundary>
            )}

            {quantityPerOrderLimit && (
              <span className='tw:block'>Limit {quantityPerOrderLimit} per order</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Using dayjs for date/time handling

const days = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

const CutoffLabel = ({ now = dayjs() }) => {
  const { cutoffTimestamp } = useSelector((state: AppState) => state.site.shipping);
  const flags = useSelector((state: AppState) => state.site.flags);

  const cutoff = useMemo(() => dayjs(cutoffTimestamp), [cutoffTimestamp]);
  const isBeforeCutoff = cutoff.isAfter(now);
  const isCutoffSameDay = cutoff.isSame(now, 'day');

  const cutoffLabel = useMemo(() => {
    const hours = cutoff.format('h');
    const minutes = cutoff.minute();
    const seconds = cutoff.second();
    const ampm = cutoff.format('A').toLowerCase();

    // Cutoff label without timezone
    const cutoffLabel =
      (seconds > 0 ? [hours, minutes, seconds] : minutes > 0 ? [hours, minutes] : [hours]).join(':') + ampm;

    return cutoffLabel;
  }, [cutoff]);

  const includeSaturday = flags.readyToShip?.[0]?.includeSaturday || false;
  const includeSunday = flags.readyToShip?.[0]?.includeSunday || false;

  const { nextLabel, isBusinessDay } = useMemo(() => {
    const day = now.day();

    const businessDays = [1, 2, 3, 4, 5];
    if (includeSaturday) businessDays.push(6);
    if (includeSunday) businessDays.unshift(0);
    const isBusinessDay = businessDays.includes(day);

    const nextDay = businessDays.find((d) => d > day) || businessDays[0];

    const nextLabel = day + 1 === nextDay || (day === 6 && nextDay === 0) ? 'Tomorrow' : days[nextDay];

    return { nextLabel, isBusinessDay };
  }, []);

  if (!cutoffTimestamp || !flags?.readyToShip) return <>Ready to Ship</>;

  return (
    <div>
      {isBusinessDay && isCutoffSameDay && isBeforeCutoff ? (
        <>
          Order by {cutoffLabel}
          <br />
          Ships Today!
        </>
      ) : (
        <>Ships {nextLabel}</>
      )}
    </div>
  );
};

export default memo(ProductAvailability);
